import { useInfiniteQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { companyProjectsQueryOptions } from '@/entities/project'
import { ApiError } from '@/shared/api'
import { flattenPages } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { StatusScreen } from '@/shared/ui/status-screen'
import { Table, TBody, TD, TH, THead, TR } from '@/shared/ui/table'

// Выданные заказчику проекты его компании — таблица безопасных колонок
// (без тегов, сумм и приоритета). Живёт в табе «Проекты» хаба заказчика.
export function CustomerProjects({ companyId }: { companyId: string }) {
  const q = useInfiniteQuery(companyProjectsQueryOptions(companyId))

  if (q.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  // Заказчику без выданных сделок бэк отвечает 403 (existence-hiding) — это не
  // ошибка, а пустое состояние.
  if (q.error) {
    if (q.error instanceof ApiError && q.error.isForbidden) {
      return (
        <EmptyState
          title="Проектов пока нет"
          description="Вам ещё не выдали доступ к проектам. Обратитесь к менеджеру."
        />
      )
    }
    return <StatusScreen title="Не удалось загрузить проекты" message="Обновите страницу." />
  }

  const projects = flattenPages(q.data.pages)
  if (projects.length === 0) {
    return (
      <EmptyState title="Проектов пока нет" description="Здесь появятся выданные вам проекты." />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <THead>
          <TR>
            <TH>Проект</TH>
            <TH>Этап</TH>
            <TH>Статус</TH>
          </TR>
        </THead>
        <TBody>
          {projects.map((project) => (
            <TR key={project.id}>
              <TD>
                <Link
                  to="/$company/$project"
                  params={{ company: project.company_slug, project: project.slug }}
                  className="font-medium text-text-primary hover:text-accent hover:underline"
                >
                  {project.title}
                </Link>
              </TD>
              <TD>
                <Chip variant="soft">{project.stage_name}</Chip>
              </TD>
              <TD>
                {project.outcome ? (
                  <StatusBadge status="completed" />
                ) : (
                  <StatusBadge status="in_progress" />
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {q.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
          >
            {q.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
          </Button>
        </div>
      )}
    </div>
  )
}
